/**
 * A fila da estação e os avisos ao iniciar uma etapa.
 *
 * Duas decisões governam este arquivo, e as duas vêm do PRD:
 *
 *  1. **A ordem é sugestão, não trilho** (RN-013). A fila mostra o que o
 *     sistema acha que vem primeiro; o operador começa o que quiser. Fora de
 *     ordem gera aviso e registro, nunca bloqueio — quem está na máquina sabe
 *     de coisas que a OS não conta.
 *  2. **A regra não fecha o caminho legítimo em silêncio** (lição do Amparo).
 *     Só duas situações realmente impedem: OS cancelada e pessoa sem permissão
 *     no setor. Todo o resto é aviso.
 */

import type { EstadoEtapa, Os, OsEtapa, Parametros, Pessoa } from './tipos.js';

const ms = (iso: string): number => Date.parse(iso);

/**
 * A ordem das seções no fluxo — PRESET da Gráfica Boa União. Outra empresa tem
 * outras seções: as que não estiverem aqui vão para o fim (peso 9), e a ordem
 * entre elas cai na sequência da própria OS. Nada quebra.
 */
const ORDEM_DAS_SECOES: Record<string, number> = {
  pre_impressao: 0,
  impressao: 1,
  acabamento: 2,
};

export interface ItemDaFila {
  etapa: OsEtapa;
  os: Os;
  estado: EstadoEtapa;
}

/**
 * A ordem da fila (RN-033):
 *
 *  1. o que o encarregado ordenou à mão;
 *  2. o que ele fixou como prioridade, o mais antigo fixado primeiro;
 *  3. previsão de entrega;
 *  4. emissão;
 *  5. número da OS, para não haver empate.
 *
 * O que já está em execução sobe ao topo: quem está com a máquina montada
 * precisa achar o cartão sem procurar.
 */
export function ordenarFila(itens: readonly ItemDaFila[]): ItemDaFila[] {
  const pesoStatus = (e: EstadoEtapa): number => {
    if (e.status === 'em_execucao') return 0;
    if (e.status === 'pausada') return 1;
    return 2;
  };
  return [...itens].sort((a, b) => {
    const ps = pesoStatus(a.estado) - pesoStatus(b.estado);
    if (ps !== 0) return ps;

    const oa = a.etapa.ordemManual;
    const ob = b.etapa.ordemManual;
    if (oa !== null && ob !== null && oa !== ob) return oa - ob;
    if (oa !== null && ob === null) return -1;
    if (oa === null && ob !== null) return 1;

    const pa = a.os.prioridadeEm;
    const pb = b.os.prioridadeEm;
    if (pa && pb && pa !== pb) return ms(pa) - ms(pb);
    if (pa && !pb) return -1;
    if (!pa && pb) return 1;

    const pe = ms(a.os.previsaoEntregaEm) - ms(b.os.previsaoEntregaEm);
    if (pe !== 0) return pe;

    const em = ms(a.os.emitidaEm) - ms(b.os.emitidaEm);
    if (em !== 0) return em;

    return a.os.numero - b.os.numero;
  });
}

export type GravidadeAviso = 'bloqueio' | 'aviso';

export interface Aviso {
  gravidade: GravidadeAviso;
  codigo: string;
  texto: string;
}

export interface ContextoAoIniciar {
  etapa: OsEtapa;
  os: Os;
  /** Todas as etapas da OS, para saber o que vem antes. */
  etapasDaOs: readonly OsEtapa[];
  /** Estado já calculado de cada etapa da OS, por id. */
  estados: ReadonlyMap<string, EstadoEtapa>;
  pessoa: Pessoa | null;
  /** Quantas etapas já estão em execução nesta estação. */
  emExecucaoNaEstacao: number;
  capacidadeParalela: number;
  /** Em quantas outras etapas esta pessoa já está agora. */
  etapasAbertasDaPessoa: number;
  parametros: Pick<
    Parametros,
    'acabamentoEsperaImpressoes' | 'operadorEmVariasEtapas' | 'operadorVeTodosSetores'
  >;
}

/**
 * O que dizer antes de deixar começar.
 *
 * Devolve a lista inteira, com gravidade. A tela mostra tudo; se houver algum
 * `bloqueio`, o botão não age. Um `aviso` vira confirmação de um toque, e a
 * confirmação fica registrada no evento.
 */
export function avisosAoIniciar(ctx: ContextoAoIniciar): Aviso[] {
  const avisos: Aviso[] = [];
  const { etapa, os, etapasDaOs, estados, pessoa, parametros } = ctx;

  if (os.canceladaEm) {
    avisos.push({
      gravidade: 'bloqueio',
      codigo: 'os_cancelada',
      texto: 'Esta OS foi cancelada. Procure o encarregado antes de produzir.',
    });
  }

  if (etapa.tipoLinha !== 'operacao') {
    avisos.push({
      gravidade: 'bloqueio',
      codigo: 'linha_nao_e_operacao',
      texto: 'Esta linha é lançamento de custo, não uma tarefa. Não há o que apontar.',
    });
  }

  if (etapa.aClassificar) {
    avisos.push({
      gravidade: 'aviso',
      codigo: 'estacao_a_classificar',
      texto: 'Esta etapa ainda não tem estação definida. O encarregado precisa classificá-la.',
    });
  }

  if (pessoa && pessoa.setores.length > 0 && etapa.setorId && !pessoa.setores.includes(etapa.setorId)) {
    avisos.push({
      gravidade: 'bloqueio',
      codigo: 'setor_nao_permitido',
      texto: 'Você não está liberado para apontar neste setor.',
    });
  }

  if (etapa.marcadaVerificar) {
    avisos.push({
      gravidade: 'aviso',
      codigo: 'os_alterada',
      texto: 'A OS foi alterada depois que esta etapa começou. Confira a ficha antes de seguir.',
    });
  }

  const estadoDela = estados.get(etapa.id);
  if (estadoDela && estadoDela.status === 'em_execucao') {
    avisos.push({
      gravidade: 'aviso',
      codigo: 'ja_em_execucao',
      texto: 'Esta etapa já está em execução. Você entra como segundo operador.',
    });
  }
  if (estadoDela && estadoDela.status === 'concluida') {
    avisos.push({
      gravidade: 'aviso',
      codigo: 'ja_concluida',
      texto: 'Esta etapa já foi concluída. Iniciar de novo reabre o apontamento.',
    });
  }

  // A etapa anterior da mesma seção.
  const naSecao = etapasDaOs
    .filter((e) => e.secao === etapa.secao && e.tipoLinha === 'operacao')
    .sort((a, b) => a.sequencia - b.sequencia);
  const posicao = naSecao.findIndex((e) => e.id === etapa.id);
  const anterior = posicao > 0 ? naSecao[posicao - 1] : undefined;
  if (anterior) {
    const s = estados.get(anterior.id);
    if (!s || (s.status !== 'concluida' && s.status !== 'pulada')) {
      avisos.push({
        gravidade: 'aviso',
        codigo: 'anterior_nao_concluida',
        texto: `A etapa anterior ("${anterior.nomeOs}") ainda não foi concluída.`,
      });
    }
  }

  // Acabamento esperando as impressões (PRD §17.1 J-5 — a confirmar).
  // PRESET da GBU: só dispara se a empresa nomear as seções assim e ligar o
  // parâmetro. Sem esses nomes, simplesmente não há regra — nada quebra.
  if (etapa.secao === 'acabamento' && parametros.acabamentoEsperaImpressoes) {
    const impressoes = etapasDaOs.filter(
      (e) => e.secao === 'impressao' && e.tipoLinha === 'operacao',
    );
    const faltando = impressoes.filter((e) => {
      const s = estados.get(e.id);
      return !s || (s.status !== 'concluida' && s.status !== 'pulada');
    });
    if (faltando.length > 0) {
      avisos.push({
        gravidade: 'aviso',
        codigo: 'impressao_pendente',
        texto:
          faltando.length === 1
            ? 'A impressão desta OS ainda não terminou.'
            : `${faltando.length} impressões desta OS ainda não terminaram.`,
      });
    }
  }

  if (ctx.emExecucaoNaEstacao >= ctx.capacidadeParalela) {
    avisos.push({
      gravidade: 'aviso',
      codigo: 'estacao_ocupada',
      texto:
        ctx.capacidadeParalela === 1
          ? 'Esta estação já tem uma etapa em execução.'
          : `Esta estação já tem ${ctx.emExecucaoNaEstacao} etapas em execução.`,
    });
  }

  if (!parametros.operadorEmVariasEtapas && ctx.etapasAbertasDaPessoa > 0) {
    avisos.push({
      gravidade: 'aviso',
      codigo: 'operador_ocupado',
      texto: 'Você já está com outra etapa em execução.',
    });
  }

  return avisos;
}

/** Há algum motivo para não deixar começar? */
export function temBloqueio(avisos: readonly Aviso[]): boolean {
  return avisos.some((a) => a.gravidade === 'bloqueio');
}

/**
 * O setor seguinte de uma OS: para onde o material vai depois desta etapa.
 * Serve à mensagem "próximo: Guilhotina" que aparece por três segundos quando
 * o operador conclui.
 */
export function proximaEtapa(
  etapaConcluida: OsEtapa,
  etapasDaOs: readonly OsEtapa[],
  estados: ReadonlyMap<string, EstadoEtapa>,
): OsEtapa | null {
  const peso: Record<string, number> = ORDEM_DAS_SECOES;
  const ordenadas = [...etapasDaOs]
    .filter((e) => e.tipoLinha === 'operacao')
    .sort((a, b) => {
      const d = (peso[a.secao] ?? 9) - (peso[b.secao] ?? 9);
      return d !== 0 ? d : a.sequencia - b.sequencia;
    });
  const i = ordenadas.findIndex((e) => e.id === etapaConcluida.id);
  if (i < 0) return null;
  for (let k = i + 1; k < ordenadas.length; k += 1) {
    const e = ordenadas[k];
    if (!e) continue;
    const s = estados.get(e.id);
    if (!s || (s.status !== 'concluida' && s.status !== 'pulada')) return e;
  }
  return null;
}

/**
 * A ordem da fila da expedição, que não é a ordem da fila de produção.
 *
 * Na máquina o que manda é a sequência do trabalho; na bancada de expedição o
 * trabalho já acabou e só resta a promessa ao cliente. Por isso aqui a regra é
 * curta e tem uma inversão que vale explicar:
 *
 *  1. **atrasada primeiro** — parece contraintuitivo ("já perdi o prazo, deixa
 *     para depois"), e é o contrário: o material já produzido parado na
 *     prateleira é a única parte do atraso que ainda dá para encurtar hoje;
 *  2. depois, a promessa mais próxima;
 *  3. número da OS, para não haver empate — sem isso a lista dança entre
 *     redesenhos e o cartão foge do dedo de quem ia tocar.
 *
 * Vive aqui, e não em cada tela, porque escritório e tablet mostram a MESMA
 * fila: se as duas ordenassem por conta própria, um dia divergiriam, e o
 * conferente e o balconista discutiriam sobre qual das duas está certa.
 */
export interface OsParaExpedir {
  numero: number;
  previsaoEntregaEm: string;
  atrasada: boolean;
}

export function ordenarParaExpedicao<T extends OsParaExpedir>(itens: readonly T[]): T[] {
  return [...itens].sort((a, b) => {
    if (a.atrasada !== b.atrasada) return a.atrasada ? -1 : 1;
    const prazo = a.previsaoEntregaEm.localeCompare(b.previsaoEntregaEm);
    if (prazo !== 0) return prazo;
    return a.numero - b.numero;
  });
}
